import { Navigate } from "react-router";
import {
  OVERVIEW_PERMISSIONS,
  useDefaultTenant,
  useHasAccessToMissionControl,
  useHasTenantPermission,
} from "hooks/permissions";
import { NoTenantPage } from "../noTenant";
import { useNavigateToTenantHome } from "hooks/navigation";
import { useState } from "react";
import { ValidatedTextField } from "src/components/common/inputs/validatedTextField";
import { useStringSerialiser } from "hooks/serialisers/strings";
import { StringSerialiserOptions } from "hooks/serialisers/types";
import { useIsAdmin, useTenant } from "hooks/settings.tsx";
import { heatOverridesEnabled } from "components/heatQueue/permissions";
import { OPERATIONS_FEATURE_FLAG } from "components/crane/featureFlags";
import {
  FeatureFlag,
  HeatOverrideMode,
  Permission,
  useGetTenantMetadataQuery,
} from "src/store/api/generatedApi";
import { useTenantData } from "contexts/tenant";
import { NotFound } from "components/notFound";
import { Box } from "@mui/material";
import { GlobalLoadingScreen } from "contexts/globalLoadingScreen";

type RedirectResult =
  { type: "redirect"; path: string } | { type: "default" } | { type: "denied" };

const useRedirectForPermissions = ({
  tenant,
  featureFlags,
  heatOverrideMode,
}: {
  tenant: string | null;
  featureFlags: FeatureFlag[] | undefined;
  heatOverrideMode: HeatOverrideMode | undefined;
}): RedirectResult => {
  const hasPermission = useHasTenantPermission(tenant);
  return getTenantLanding({
    tenant,
    featureFlags,
    heatOverrideMode,
    hasPermission,
  });
};

export const getTenantLanding = ({
  tenant,
  featureFlags,
  heatOverrideMode,
  hasPermission,
}: {
  tenant: string | null;
  featureFlags: FeatureFlag[] | undefined;
  heatOverrideMode: HeatOverrideMode | undefined;
  hasPermission: (permission: Permission) => boolean;
}): RedirectResult => {
  if (tenant == null) {
    return { type: "denied" };
  }

  if (OVERVIEW_PERMISSIONS.some(hasPermission)) {
    return { type: "default" };
  } else if (hasPermission("crane_driver")) {
    return { type: "redirect", path: `/${tenant}/crane/driver` };
  } else if (
    hasPermission("crane_supervisor") &&
    heatOverridesEnabled(heatOverrideMode)
  ) {
    return { type: "redirect", path: `/${tenant}/heat-queue` };
  } else if (
    hasPermission("crane_supervisor") &&
    featureFlags?.includes(OPERATIONS_FEATURE_FLAG)
  ) {
    return {
      type: "redirect",
      path: `/${tenant}/crane/supervisor/standard/latest`,
    };
  } else if (hasPermission("video_labelling")) {
    return { type: "redirect", path: `/${tenant}/tool/identify-events` };
  } else if (hasPermission("video_label_reviewing")) {
    return { type: "redirect", path: `/${tenant}/tool/manual-review` };
  } else if (hasPermission("manage_labellers")) {
    return { type: "redirect", path: `/${tenant}/manage-labellers` };
  } else if (hasPermission("crane_supervisor")) {
    return { type: "default" };
  } else {
    return { type: "denied" };
  }
};

export const RedirectToTenant = () => {
  const { defaultTenant } = useDefaultTenant();
  const hasAccessToMissionControl = useHasAccessToMissionControl();
  const isAdmin = useIsAdmin();
  const { data: tenantMetadata, isLoading } = useGetTenantMetadataQuery(
    { tenantName: defaultTenant ?? "" },
    { skip: defaultTenant == null }
  );

  const result = useRedirectForPermissions({
    tenant: defaultTenant,
    featureFlags: tenantMetadata?.feature_flags,
    heatOverrideMode: tenantMetadata?.heat_override_mode,
  });

  if (defaultTenant != null && tenantMetadata != null) {
    switch (result.type) {
      case "redirect":
        return <Navigate to={result.path} replace />;
      case "default":
      case "denied":
        return <Navigate to={`/${defaultTenant}/`} replace />;
    }
  } else if (isAdmin) {
    return <SelectTenantInput />;
  } else if (hasAccessToMissionControl) {
    return <Navigate to="/mission-control" replace />;
  } else if (!isLoading) {
    return <NoTenantPage />;
  } else {
    return <GlobalLoadingScreen />;
  }
};

const SelectTenantInput = () => {
  const [tenant, setTenant] = useState("");
  const navigate = useNavigateToTenantHome();
  const serialiser = useStringSerialiser();

  return (
    <Box sx={{ p: 4 }}>
      <ValidatedTextField<string, StringSerialiserOptions>
        serialiser={serialiser}
        value={tenant}
        setValue={setTenant}
        label="Organisation"
        onEditConfirmedWithKeyboard={(t) => {
          navigate(t);
        }}
        textFieldProps={{ autoFocus: true, autoComplete: "off" }}
      />
    </Box>
  );
};

export const TenantIndexRedirect = ({ children }: React.PropsWithChildren) => {
  const tenant = useTenant();
  const { feature_flags: featureFlags, heat_override_mode: heatOverrideMode } =
    useTenantData();

  const result = useRedirectForPermissions({
    tenant,
    featureFlags,
    heatOverrideMode,
  });

  if (result.type === "redirect") {
    return <Navigate to={result.path} replace />;
  } else if (result.type === "default") {
    return children;
  } else {
    return <NotFound />;
  }
};
